import React, { useState } from 'react';
import { Upload, Loader, Check, X } from 'lucide-react';
import * as api from '../api/client';
import seedJson from '../seed.json';

/**
 * SeedLoader
 * --------------------------------------------------------------------
 * One-time content import utility. POSTs src/cms/seed.json's `pages`
 * array to /api/website-cms/seed. Destructive (overwrites all blocks)
 * so a confirmation dialog is required.
 */
export default function SeedLoader() {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const pageCount = Array.isArray(seedJson && seedJson.pages)
    ? seedJson.pages.length
    : 0;

  const runSeed = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.seed(seedJson.pages || []);
      setResult(res || { ok: true });
      setConfirming(false);
    } catch (err) {
      setError(err && err.message ? err.message : 'Seed failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="rounded-lg border border-amber-300 bg-amber-50 p-5">
        <div className="flex items-center gap-2 text-amber-900 font-semibold">
          <Upload size={16} />
          Bulk seed loader
        </div>
        <p className="text-sm text-amber-900/80 mt-1">
          Imports <code className="font-mono">src/cms/seed.json</code> into the
          backend. This will <strong>overwrite all existing block content</strong>
          for the {pageCount} pages defined in the seed file.
        </p>

        <ul className="mt-3 text-xs text-amber-900/80 list-disc pl-5 space-y-0.5">
          {(seedJson.pages || []).map((p) => (
            <li key={p.slug}>
              <span className="font-mono">/{p.slug}</span> — {p.title}
              {Array.isArray(p.blocks) ? ` (${p.blocks.length} blocks)` : ''}
            </li>
          ))}
        </ul>

        <div className="mt-4 flex items-center gap-2">
          {!confirming ? (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-amber-600 text-white hover:bg-amber-700 text-sm"
            >
              <Upload size={14} />
              Load seed.json into backend
            </button>
          ) : (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={runSeed}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-60 text-sm"
              >
                {busy ? (
                  <Loader size={14} className="animate-spin" />
                ) : (
                  <Check size={14} />
                )}
                Yes, overwrite all content
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => setConfirming(false)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"
              >
                <X size={14} />
                Cancel
              </button>
            </>
          )}
        </div>

        {error && (
          <div className="mt-3 rounded border border-red-200 bg-red-50 text-red-700 text-xs px-3 py-2">
            {error}
          </div>
        )}
        {result && (
          <div className="mt-3 rounded border border-emerald-200 bg-emerald-50 text-emerald-800 text-xs px-3 py-2">
            <div className="font-semibold inline-flex items-center gap-1.5">
              <Check size={12} />
              Seed completed
            </div>
            <pre className="mt-1 overflow-x-auto whitespace-pre-wrap">
              {JSON.stringify(result, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}
